import { useState } from "react"
import { Modal } from "common/Modal"
import UserService from "services/user.service"
import { useNotifications } from "hooks/useNotification"

const service = new UserService()

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

type Props = {
  showModal: boolean
  setShowModal: (v: boolean) => void
  onSuccess: () => void
}

export const CreateOperationModal = ({
  showModal,
  setShowModal,
  onSuccess,
}: Props) => {
  const [badgeId, setBadgeId] = useState("")
  const [monthName, setMonthName] = useState("")
  const [saving, setSaving] = useState(false)
  const { successMessage, errorMessage } = useNotifications()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!badgeId.trim() || !monthName) {
      errorMessage("Badge ID and month are required")
      return
    }
    setSaving(true)
    try {
      const { data } = await service.createOperation({
        badgeId: badgeId.trim(),
        monthName,
      })
      if (data?.success === false) {
        errorMessage(data?.message || "Could not create operation")
        return
      }
      successMessage("Operation created")
      setBadgeId("")
      setMonthName("")
      setShowModal(false)
      onSuccess()
    } catch (err: any) {
      errorMessage(err?.response?.data?.message || "Could not create operation")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      showModal={showModal}
      setShowModal={setShowModal}
      title="Create operation"
      width="w-full md:max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col gap-1.5">
          <label className="field-label">
            Badge ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={badgeId}
            onChange={(e) => setBadgeId(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500"
            placeholder="Rep badge ID"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="field-label">
            Month <span className="text-red-500">*</span>
          </label>
          <select
            value={monthName}
            onChange={(e) => setMonthName(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Select month</option>
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={() => setShowModal(false)}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  )
}
